import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { invokeArcaCreditNoteForVoidedSale } from '../../sync/core/fiscalSideEffects';
import { invalidateCashSummaryCaches } from '../../cash/infra/cashSummaryRemoteCache';
import { invalidateSalesListCachesForSale } from '../infra/salesListInvalidation';
import { syncCatalogSnapshotAfterLocalWrite } from '../../catalog/infra/catalogSnapshotSync';

export type SaleVoidRemoteParams = {
  saleId: string;
  branchId: string;
  voidReason: string;
  voidedByUserId?: string | null;
  fiscalMode?: string;
  supervisorOverrideId?: string;
};

/**
 * Path único de anulación (ADR-017 online-only).
 * Antes existía un intake local + cola sync; ese camino estaba muerto (stub throw).
 */
export async function executeSaleVoidRemoteIntake(params: SaleVoidRemoteParams): Promise<void> {
  addSentryBreadcrumb('pos.sale_void', 'apply_sale_void_command invoked', 'info', {
    saleId: params.saleId,
    branchId: params.branchId,
    fiscalMode: params.fiscalMode ?? null,
  });
  const { voidSaleRemote } = await import('../infra/salesRemoteVoidAdapter');
  await voidSaleRemote(params);

  void logBusinessAuditEvent('pos.sale.void', {
    branchId: params.branchId,
    saleId: params.saleId,
    voidReason: params.voidReason,
    voidedByUserId: params.voidedByUserId,
    remote: true,
  });

  try {
    const { pullCustomerDataFromSupabaseApplication } = await import('../../sync/public-api');
    await pullCustomerDataFromSupabaseApplication();
  } catch (err) {
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale_void.pull_customers',
      error: err,
      ingestError: err instanceof Error ? err.message : null,
      applied: true,
      itemId: params.saleId,
      extra: { saleId: params.saleId, branchId: params.branchId },
    });
  }

  try {
    await syncCatalogSnapshotAfterLocalWrite();
  } catch (err) {
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale_void.pull_stock',
      error: err,
      ingestError: err instanceof Error ? err.message : null,
      applied: true,
      itemId: params.saleId,
      extra: { saleId: params.saleId, branchId: params.branchId },
    });
  }

  // Fiscal NC: source of truth = pos.fiscal_jobs + worker. Client invoke = kick.
  if (params.fiscalMode === 'fiscal') {
    try {
      await invokeArcaCreditNoteForVoidedSale(params.saleId);
    } catch (err) {
      captureOpsFailure({
        module: 'pos',
        op: 'pos.sale_void.credit_note_kick',
        error: err,
        ingestError: err instanceof Error ? err.message : null,
        applied: true,
        itemId: params.saleId,
        extra: { saleId: params.saleId, branchId: params.branchId, role: 'kick' },
      });
    }
  }

  invalidateSalesListCachesForSale(params.branchId, params.saleId);
  invalidateCashSummaryCaches(params.branchId);
}
